import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { InformationTypeEnum } from 'src/app/enum/shopping-cart/information-type.enum';
import { ShoppingCartElementEnum } from 'src/app/enum/shopping-cart/shopping-cart-element.enum';
import { Application } from 'src/app/models/configurators-2d/administrations/application';
import { Order } from 'src/app/models/configurators-2d/order';
import { ShoppingCartProduct } from 'src/app/models/shopping-cart/shopping-cart-product';
import { ShoppingCartProductInformation } from 'src/app/models/shopping-cart/shopping-cart-product-information';
import { ShoppingCartProductList } from 'src/app/models/shopping-cart/shopping-cart-product-list';
import { IShoppingCartService } from 'src/app/services/configurators-2d/shopping-cart/i-shopping-cart-service';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { faCheck, faCommentAlt, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { ShoppingCartDiscount } from 'src/app/models/shopping-cart/shopping-cart-discount';
import { ShoppingCartAssistance } from 'src/app/models/shopping-cart/shopping-cart-assistance';
import { ShoppingCartProductAccessory } from 'src/app/models/shopping-cart/shopping-cart-product-accessory';
import { getPriceHtFromTtc, getPriceTtcFromHtAndTva } from 'src/app/services/helpers/formula-helper';
import { ShoppingCartProductCategory } from 'src/app/models/shopping-cart/shopping-cart-product-category';
import { cloneDeep } from '../../services/helpers/utils';

@Component({
  selector: 'ng-casto-shopping-cart',
  templateUrl: './shopping-cart.component.html',
})
export class ShoppingCartComponent implements OnInit, OnChanges {
  /** Rechargement de la page */
  @Input() refresh: boolean;

  /** service implémentant IShoppingCartService */
  @Input() shoppingCartService: IShoppingCartService;

  /** Liste des produits */
  @Input() shoppingCartProductList: ShoppingCartProductList;

  /** Liste des remises */
  @Input() discounts: ShoppingCartDiscount[];

  /** Dernière commande effectuée */
  @Input() order: Order;

  /** Indique s'il y a eu des modifications après passage de commande */
  @Input() configurationHasChanged: boolean;

  /** Services sélectionnés */
  @Input() selectedAssistances: ShoppingCartAssistance[];

  /** Pour revenir à une page précédente */
  @Output() previous: EventEmitter<{ externalId: number; numberPagePrevious: number }> = new EventEmitter<{
    externalId: number;
    numberPagePrevious: number;
  }>();

  /** Mise à jour de la liste des produits */
  @Output() productListUpdate: EventEmitter<any> = new EventEmitter<ShoppingCartProductList>();

  /** Notification de la fin du rechargement de la page */
  @Output() refreshFinished: EventEmitter<any> = new EventEmitter<boolean>();

  /** Mise à jour de la date de dernière modification utilisateur */
  @Output() configurationHasChangedUpdate: EventEmitter<any> = new EventEmitter<boolean>();

  // Application liée
  application: Application;

  // Icônes
  faCommentAlt = faCommentAlt;
  faCheck = faCheck;
  faHourGlass = faHourglassHalf;

  /** Liste des produits */
  productList: ShoppingCartProductList;

  /** En cours de chargement */
  loader: boolean = false;

  /** En cours de chargement commande */
  orderLoad: [string, number] = ['', -1];

  /** Type d'information (observation ou option) */
  informationTypeEnum = InformationTypeEnum;

  /** Présence d'une remise expirée */
  discountExpired: boolean = false;

  /** Modification après un passage de commande */
  configurationHasChangedSC: boolean;

  /**url des images */
  baseImgUrl: string;

  /** Enum des contextes de configurations */
  readonly CONFIG_CONTEXT = CONFIG_CONTEXT;

  constructor(public modalService: ModalService) { }

  ngOnInit(): void {
    this.loader = true;
    this.setComponent().then(() => {
      this.baseImgUrl = this.shoppingCartService.getBaseImgUrl();
      this.loader = false;
    });
  }

  /** Réinitialiser le component lors du changement d'url projet */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.refresh?.currentValue === true) {
      this.ngOnInit();
      this.refreshFinished.emit();
    }
  }

  /** Réinitialisation du component */
  async setComponent(): Promise<void> {
    this.productList = this.shoppingCartProductList;

    for (const category of this.productList.shoppingCartProductCategories) {
      for (const product of category.shoppingCartProducts) {
        //Remises disponibles
        product.availableDiscounts = this.availableDiscounts(product.productCode);

        //Accessoires disponibles
        product.accessoriesAvailable =
          await this.shoppingCartService.getAccessoriesAvailable(product.productCode);
      }
    }

    if (this.discounts) {
      //vérification si remise expirée
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (this.discounts.find(d => new Date(d.validityEndDate) < today) != undefined) {
        this.discountExpired = true;
      }
    }

    this.configurationHasChangedSC = this.configurationHasChanged;
  }

  /** Services */

  /**
   * Fonction pour synchroniser les changements fait sur les services dans carpentryComponent
   */
  synchroAssistances(): void {
    this.productList.shoppingCartAssistances = this.selectedAssistances;
  }

  /**
   * Fonction d'ajout d'un service
   */
  addAssistance(): void {
    this.synchroAssistances();
    this.modalService.display(MODAL.SHOPPING_CART_ADD_ASSISTANCE, {
      existingAssistances: this.productList.shoppingCartAssistances,
      appService: this.shoppingCartService,
      editAssistance: undefined,
      onClose: assistance => {
        this.modalService.close();
        if (assistance != undefined) {
          if (this.productList.shoppingCartAssistances == undefined) {
            this.productList.shoppingCartAssistances = [];
          }
          this.productList.shoppingCartAssistances.push(assistance);
          this.productList.shoppingCartAssistances.sort((a, b) =>
            a.assistanceType.label.localeCompare(b.assistanceType.label)
          );
          this.productListUpdate.emit(this.productList);
        }
      },
    });
  }

  /**
   * Fonction de modification d'un service appliqué
   * @param assistance
   */
  editAssistance(assistance: ShoppingCartAssistance): void {
    this.synchroAssistances();
    this.modalService.display(MODAL.SHOPPING_CART_ADD_ASSISTANCE, {
      existingAssistances: this.productList.shoppingCartAssistances.filter(
        s => s.assistanceType.id !== assistance.assistanceType.id
      ),
      appService: this.shoppingCartService,
      editAssistance: assistance,
      onClose: assistance => {
        this.modalService.close();
        this.productList.shoppingCartAssistances = this.productList.shoppingCartAssistances.map(s => {
          if (s.assistanceType.id === assistance.assistanceType.id) {
            return assistance;
          } else {
            return s;
          }
        });
        this.productList.shoppingCartAssistances.sort((a, b) =>
          a.assistanceType.label.localeCompare(b.assistanceType.label)
        );

        this.productListUpdate.emit(this.productList);
      },
    });
  }

  /**
   * Fonction de suppression d'un service
   * @param id
   * @param event
   */
  deleteAssistance(id: number, event: Event): void {
    event.stopPropagation();
    this.synchroAssistances();
    this.modalService.display(MODAL.SHOPPING_CART_PRODUCT_DELETE, {
      element: ShoppingCartElementEnum.Assistance,
      onClose: deleted => {
        this.modalService.close();
        if (deleted) {
          this.productList.shoppingCartAssistances = this.productList.shoppingCartAssistances.filter(
            s => s.assistanceType.id != id
          );
          if (this.productList.shoppingCartAssistances.length === 0) {
            this.productList.shoppingCartAssistances = undefined;
          }
          this.productListUpdate.emit(this.productList);
        }
      },
    });
  }

  /**
   * Fonction pour déterminer le prix total des services
   */
  totalAssistancePrice(): number {
    let total: number = 0;
    for (const assistance of this.selectedAssistances) {
      total += assistance.priceTtc;
    }
    return total;
  }

  /** Remises */
  /**
   * Fonction d'affichage des remises possible pour le produit
   * @param productCode
   * @returns
   */
  availableDiscounts(productCode: string): ShoppingCartDiscount[] {
    if (this.discounts) {
      return this.discounts
        .filter(d => d.productCode === productCode)
        .sort((a, b) => a.discountValue - b.discountValue);
    }
    return [];
  }

  /**
   * Fonction de vérification de validité d'une remise
   * @param discount
   * @returns
   */
  isDiscountExpired(discount: ShoppingCartDiscount): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(discount.validityEndDate) < today;
  }

  /** Fonction de vérification de validité d'une remise appliquée à un produit */
  isProductDiscountExpired(product: ShoppingCartProduct): boolean {
    return this.isDiscountExpired(
      this.discounts.find(d => d.productCode === product.productCode && d.discountValue === product.discount)
    );
  }

  /** Mise à jour du produit suite à la sélection d'une remise */
  updateDiscount(product: ShoppingCartProduct): void {
    let discount = this.availableDiscounts(product.productCode).find(d => d.ean === product.ean);
    product.discount = discount.discountValue;
    this.productListUpdate.emit(this.productList);

    this.configurationHasChangedSC = true;
    this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
  }

  /** Gestion des commandes */

  /** Fonction de vérification d'envoi fournisseur */
  isSupplierSent(): boolean {
    return this.productList.shoppingCartProductCategories.some(c =>
      c.shoppingCartProducts.some(p => p.supplierSentLastDate)
    );
  }

  /** Fonction de mise à jour des numéros de commande */
  async orderNumberEdition(): Promise<void> {
    if (!this.isOrderNumberEditionDisabled()) {
      await this.shoppingCartService.prepareOrderNumberEdition(this.productList);
      this.modalService.display(MODAL.SHOPPING_CART_ORDER_NUMBER_EDITION, {
        productList: this.productList,
        service: this.shoppingCartService,
        order: this.order,
        onClose: productList => {
          this.modalService.close();
          if (productList != undefined) {
            this.productList = productList;
            this.productListUpdate.emit(this.productList);
          }
        },
      });
    }
  }

  /** Fonction de désactivation du bouton Edition des numéros de commande */
  isOrderNumberEditionDisabled(): boolean {
    return this.order && this.configurationHasChanged;
  }

  /** Gestion des produits */

  /** Ajout d'un produit */
  addQuotation(): void {
    this.previous.emit({ externalId: undefined, numberPagePrevious: 2 });
  }

  /**
   * Duplication d'un produit
   * @param product
   * @param category
   */
  async duplicateProduct(product: ShoppingCartProduct, category: ShoppingCartProductCategory): Promise<void> {
    // duplication du produit
    let newProduct = cloneDeep<ShoppingCartProduct>(product);
    delete newProduct.id;
    // duplication
    this.shoppingCartService.duplicate(newProduct).then(externalId => {
      newProduct.externalId = externalId;
      if (newProduct.externalId != null) {
        // ajout du produit dans la liste
        this.productList.shoppingCartProductCategories
          .find(c => c.name === category.name)
          .shoppingCartProducts.push(newProduct);

        this.productListUpdate.emit(this.productList);

        // on indique que la commande est changé
        this.configurationHasChangedSC = true;
        this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
      }
    });
  }

  /** Modification d'un produit */
  editQuote(externalId: number): void {
    this.previous.emit({ externalId: externalId, numberPagePrevious: 2 });
  }

  /** Suppression d'un produit */
  deleteProduct(externalId: number, event: Event): void {
    event.stopPropagation();
    this.modalService.display(MODAL.SHOPPING_CART_PRODUCT_DELETE, {
      element: ShoppingCartElementEnum.Product,
      onClose: deleted => {
        this.modalService.close();
        if (deleted) {
          this.productList.shoppingCartProductCategories.forEach(category => {
            category.shoppingCartProducts = category.shoppingCartProducts.filter(
              product => product.externalId !== externalId
            );
          });
          this.productList.shoppingCartProductCategories = this.productList.shoppingCartProductCategories.filter(
            c => c.shoppingCartProducts.length > 0
          );
          if (this.productList.shoppingCartProductCategories.length == 0) {
            this.productList.shoppingCartAssistances = [];
            this.productListUpdate.emit(this.productList);
          }

          this.productListUpdate.emit(this.productList);

          this.configurationHasChangedSC = true;
          this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
        }
      },
    });
  }

  /** Mise à jour de la quantité */
  updateQuantity(product: ShoppingCartProduct) {
    this.modalService.display(MODAL.SHOPPING_CART_QUANTITY_UPDATE, {
      quantity: product.quantity,
      onClose: async quantity => {
        this.modalService.close();
        if (quantity > 0) {
          this.orderLoad[0] = this.productList.shoppingCartProductCategories.filter(
            s => s.shoppingCartProducts.filter(p => p.externalId === product.externalId)[0]
          )[0].name;
          this.orderLoad[1] = product.externalId;
          product.quantity = quantity;
          let refresh: number = await this.shoppingCartService.updateQuantity(product);
          if (refresh != undefined) {
            const category = this.productList.shoppingCartProductCategories.find(c => c.name === this.orderLoad[0]);
            let selectedProduct = category.shoppingCartProducts.find(p => p.externalId === product.externalId);
            selectedProduct.externalId = refresh;
            await this.shoppingCartService.getProductDetails(selectedProduct);
          }

          this.orderLoad = ['', -1];
          this.productListUpdate.emit(this.productList);

          this.configurationHasChangedSC = true;
          this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
        }
      },
    });
  }

  /** Mise à jour de la tva d'un produit */
  updateTva(product: ShoppingCartProduct) {
    this.modalService.display(MODAL.SHOPPING_CART_VAT_UPDATE, {
      vats: this.shoppingCartService.getProductVats(),
      selectedVat: product.tva,
      onClose: async tva => {
        this.modalService.close();
        if (tva != undefined && tva != product.tva) {
          this.orderLoad[0] = this.productList.shoppingCartProductCategories.filter(
            s => s.shoppingCartProducts.filter(p => p.externalId === product.externalId)[0]
          )[0].name;
          this.orderLoad[1] = product.externalId;
          product.tva = tva;
          let refresh: number = await this.shoppingCartService.updateTva(product);
          if (refresh != undefined) {
            const category = this.productList.shoppingCartProductCategories.find(c => c.name === this.orderLoad[0]);
            let selectedProduct = category.shoppingCartProducts.find(p => p.externalId === product.externalId);
            selectedProduct.externalId = refresh;
            for (const accessory of selectedProduct.accessories) {
              //on met à jour la tva des accessoires avec celle des produits
              let unitPrice: number = getPriceHtFromTtc(accessory.accessory.priceTtc, accessory.tva);
              accessory.tva = tva;
              accessory.accessory.priceTtc = getPriceTtcFromHtAndTva(unitPrice, tva);
            }
          }

          this.orderLoad = ['', -1];
          this.productListUpdate.emit(this.productList);

          this.configurationHasChangedSC = true;
          this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
        }
      },
    });
  }

  /** Fonction d'affichage de l'observation client */
  displayObservations(observation: ShoppingCartProductInformation): void {
    if (observation != undefined) {
      this.modalService.display(MODAL.SHOPPING_CART_INFORMATIONS, {
        informations: [observation.label],
        onClose: () => {
          this.modalService.close();
        },
      });
    }
  }

  /** Accessoires */

  /** Recalcul du prix ttc unitaire d'un accessoire */
  getAccessoryUnitPriceTtc(accessory: ShoppingCartProductAccessory): number {
    return getPriceTtcFromHtAndTva(
      getPriceHtFromTtc(accessory.accessory.priceTtc, accessory.accessory.tva),
      Number(accessory.tva)
    );
  }

  /** Recalcul du prix ttc total d'un accessoire */
  getAccessoryTotalPriceTtc(accessory: ShoppingCartProductAccessory): number {
    return this.getAccessoryUnitPriceTtc(accessory) * accessory.quantity;
  }

  /** Suppression d'un accessoire */
  deleteAccessory(externalId: number, accessoryId: string): void {
    this.modalService.display(MODAL.SHOPPING_CART_PRODUCT_DELETE, {
      element: ShoppingCartElementEnum.Accessory,
      onClose: deleted => {
        this.modalService.close();
        if (deleted) {
          this.productList.shoppingCartProductCategories.forEach(c => {
            c.shoppingCartProducts.forEach(p => {
              p.accessories = p.accessories.filter(
                a => !(p.externalId === externalId && a.accessoryId === accessoryId)
              );
            });
          });

          this.productListUpdate.emit(this.productList);

          this.configurationHasChangedSC = true;
          this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
        }
      },
    });
  }

  /** Ajout d'un accessoire */
  addAccessory(externalId: number): void {
    this.previous.emit({ externalId: externalId, numberPagePrevious: 1 });
  }

  /** Mise à jour de la tva d'un accessoire */
  updateTvaAccessory(product: ShoppingCartProduct, accessory: ShoppingCartProductAccessory) {
    this.modalService.display(MODAL.SHOPPING_CART_VAT_UPDATE, {
      vats: this.shoppingCartService.getProductVats(),
      selectedVat: accessory.tva,
      onClose: async (tva: number) => {
        this.modalService.close();
        if (tva != undefined && tva != accessory.tva) {
          accessory.tva = tva;
          this.productListUpdate.emit(this.productList);

          this.configurationHasChangedSC = true;
          this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
        }
      },
    });
  }

  /** Mise à jour de la quantité d'un accessoire */
  updateQuantityAccessory(accessory: ShoppingCartProductAccessory) {
    this.modalService.display(MODAL.SHOPPING_CART_QUANTITY_UPDATE, {
      quantity: accessory.quantity,
      onClose: async (quantity: number) => {
        this.modalService.close();
        if (quantity > 0) {
          accessory.quantity = quantity;
          this.productListUpdate.emit(this.productList);

          this.configurationHasChangedSC = true;
          this.configurationHasChangedUpdate.emit(this.configurationHasChangedSC);
        }
      },
    });
  }

  getSortedOptions(product) {
    return product.informations.sort((a, b) => a.order - b.order);
  }
}
